import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Permissions } from '@air/api';
import { savePermissionsForObjectsToStore } from '@air/hook-use-fetch-objects-permissions';
import { callRequestWithRetry } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { GetPrivateUploadUrlsManager } from '~/components/Upload/utils/GetPrivateUploadUrlsManager';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { useAirStore } from '~/utils/ReduxUtils';

type PrivateUploadUrlsManagerProviderProps = {
  children: ReactNode;
};

type PrivateUploadUrlsManagerProviderContextValue = {
  manager: GetPrivateUploadUrlsManager;
};

const defaultValue: PrivateUploadUrlsManagerProviderContextValue = {
  manager: null as unknown as GetPrivateUploadUrlsManager,
};

const PrivateUploadUrlsManagerProviderContext = createContext(defaultValue);

export const PrivateUploadUrlsManagerProvider = memo(({ children }: PrivateUploadUrlsManagerProviderProps) => {
  const store = useAirStore();
  const dispatch = useDispatch();

  const value = useMemo<PrivateUploadUrlsManagerProviderContextValue>(
    () => ({
      manager: new GetPrivateUploadUrlsManager({
        store,
        onUploadUrlsFetched: async ({ metadata, response }) => {
          const objects = {
            clipIds: response.map((u) => u.id),
            assetIds: response.map((u) => u.assetId),
          };

          const permissions = await callRequestWithRetry(() =>
            Permissions.getFromObjects({
              mode: 'all',
              objects,
              workspaceId: metadata.workspaceId,
            }),
          );

          savePermissionsForObjectsToStore(objects, permissions, dispatch);
        },
      }),
    }),
    [store, dispatch],
  );

  return (
    <PrivateUploadUrlsManagerProviderContext.Provider value={value}>
      {children}
    </PrivateUploadUrlsManagerProviderContext.Provider>
  );
});

PrivateUploadUrlsManagerProvider.displayName = 'PrivateUploadUrlsManagerProvider';

export const usePrivateUploadUrlsManager = () => {
  const context = useContext(PrivateUploadUrlsManagerProviderContext);

  if (context === defaultValue) {
    const error = 'PrivateUploadUrlsManagerProviderContext used outside of PrivateUploadUrlsManagerProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
